import React from "react";
import { Calendar, Info } from "lucide-react";
import { toast } from "react-toastify";
import EventVenueInfoSection from "./EventVenueInfoSection";
import ThumbnailImageUpload from "./ThumbnailImageUpload";
import FeaturedImagesUpload from "./FeaturedImagesUpload";
import AboutEventSection from "./AboutEventSection";
import PerformersEntertainersSection from "./PerformersEntertainersSection";
import SponsorsSection from "./SponsorsSection";
import EventScheduleSection from "./EventScheduleSection";

const StepTwoEventInformation = ({ formData, onUpdate, onBack, onNext }) => {
  const handleSectionUpdate = (sectionData) => {
    onUpdate({
      ...formData,
      ...sectionData,
    });
  };

  const handleNext = () => {
    if (!formData.eventName || formData.eventName.trim() === "") {
      toast.error("Event title is required");
      return;
    }
    if (!formData.eventType || formData.eventType.length === 0) {
      toast.error("Please select at least one event type");
      return;
    }
    if (!formData.eventStartDate) {
      toast.error("Start date is required");
      return;
    }
    if (!formData.eventEndDate) {
      toast.error("End date is required");
      return;
    }
    if (formData.eventStartDate && formData.eventEndDate && formData.eventEndDate < formData.eventStartDate) {
      toast.error("End date must be on or after start date");
      return;
    }
    if (!formData.eventStartTime) {
      toast.error("Start time is required");
      return;
    }
    if (!formData.eventEndTime) {
      toast.error("End time is required");
      return;
    }
    if (!formData.venueName) {
      toast.error("Venue name is required");
      return;
    }
    if (!formData.venueStreetAddress) {
      toast.error("Venue street address is required");
      return;
    }
    if (!formData.country) {
      toast.error("Country is required");
      return;
    }
    if (!formData.city) {
      toast.error("City/State/Region is required");
      return;
    }
    if (!formData.thumbnail) {
      toast.error("Event thumbnail is required");
      return;
    }

    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
              Event Information
            </h2>
            <p className="mt-0.5 text-xs text-gray-600 sm:text-sm">
              Provide details about your event and venue
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="p-3 mt-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-start space-x-2">
            <Info className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-purple-800">
              <p className="mb-0.5 font-semibold">💡 Quick Tips:</p>
              <ul className="space-y-0.5 text-xs list-disc list-inside">
                <li>Select 1-2 event types that best describe your event</li>
                <li>Ensure all venue information is accurate</li>
                <li>Google location helps attendees find the venue easily</li>
                <li>All fields marked with * are required</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Event and Venue Information Section */}
      <EventVenueInfoSection data={formData} onUpdate={handleSectionUpdate} />

      {/* Thumbnail Image Upload */}
      <ThumbnailImageUpload
        value={formData.thumbnail}
        onChange={(value) => handleSectionUpdate({ thumbnail: value })}
        maxSize={5}
        acceptedFormats={["image/jpeg", "image/jpg", "image/png", "image/webp"]}
      />

      {/* Featured Images Upload */}
      <FeaturedImagesUpload
        value={formData.featuredImages || []}
        onChange={(value) => handleSectionUpdate({ featuredImages: value })}
        maxSize={5}
        acceptedFormats={["image/jpeg", "image/jpg", "image/png", "image/webp"]}
      />

      {/* About Event Section */}
      <AboutEventSection
        value={formData.aboutEvent || ""}
        onChange={(value) => handleSectionUpdate({ aboutEvent: value })}
      />

      {/* Performers/Entertainers Section */}
      <PerformersEntertainersSection
        value={formData.performers || []}
        onChange={(value) => handleSectionUpdate({ performers: value })}
      />

      {/* Sponsors Section */}
      <SponsorsSection
        value={formData.sponsors || []}
        onChange={(value) => handleSectionUpdate({ sponsors: value })}
        maxSize={2}
        acceptedFormats={["image/jpeg", "image/jpg", "image/png", "image/webp"]}
      />

      {/* Event Schedule Section */}
      <EventScheduleSection
        value={formData.agenda || []}
        onChange={(value) => handleSectionUpdate({ agenda: value })}
      />

      {/* Navigation Buttons */}
      <div className="flex flex-col gap-3 pt-4 border-t border-gray-200 sm:flex-row">
        <button
          onClick={onBack}
          className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-200 hover:bg-gray-50 rounded-lg transition-all"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          className="flex-1 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
        >
          <span>Continue to Next Step</span>
          <Calendar className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default StepTwoEventInformation;
